import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Logo } from "@/components/shared";
import { ShoppingBag, Sparkles, ArrowRight, ArrowLeft, Eye, EyeOff, Shield } from "lucide-react";

const inputClass =
  "w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-900 font-medium outline-none focus:border-orange-400 focus:bg-white focus:ring-4 focus:ring-orange-100 transition";

export default function LoginPage() {
  const { studentLogin, adminLogin } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("student"); // "student" | "admin"
  const [stud, setStud] = useState({ first_name: "", last_name: "", pin: "" });
  const [code, setCode] = useState("");
  const [showCode, setShowCode] = useState(false);
  const [showPin, setShowPin] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submitStudent = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await studentLogin(stud.first_name.trim(), stud.last_name.trim(), stud.pin.trim());
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const submitAdmin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await adminLogin(code.trim());
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* visual side */}
      <div className="hidden lg:flex flex-col justify-between p-12 bg-gradient-to-br from-orange-500 via-orange-500 to-orange-600 text-white relative overflow-hidden">
        <div className="absolute -right-16 -top-16 h-72 w-72 rounded-full bg-white/10" />
        <div className="absolute -left-10 bottom-10 h-52 w-52 rounded-full bg-sky-400/30" />
        <div className="relative">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center rotate-[-6deg]">
              <ShoppingBag className="h-6 w-6 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-display text-2xl font-semibold">MerryZone</span>
          </div>
        </div>
        <div className="relative space-y-5">
          <span className="inline-flex items-center gap-2 bg-white/15 backdrop-blur px-4 py-1.5 rounded-full text-sm font-bold">
            <Sparkles className="h-4 w-4" /> le magasin du lycée
          </span>
          <h1 className="font-display text-5xl font-semibold leading-tight">
            Deviens un pro<br />du merchandising.
          </h1>
          <p className="text-orange-50 text-lg max-w-md font-medium">
            Tes cours, ton portfolio et ta progression du Novice à l'Expert. Tout pour réussir ton CAP Équipier Polyvalent du Commerce.
          </p>
        </div>
        <p className="relative text-sm text-orange-100 font-semibold">CAP EPC · Académie de Lille</p>
      </div>

      {/* form side */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm mz-rise">
          <div className="lg:hidden mb-8"><Logo size="lg" /></div>

          {mode === "student" ? (
            <>
              <h2 className="font-display text-3xl font-semibold text-slate-900">Bonjour 👋</h2>
              <p className="text-slate-500 mt-1 font-medium">Entre ton prénom et ton nom pour accéder au magasin.</p>

              <form onSubmit={submitStudent} className="mt-8 space-y-4">
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-1.5">Prénom</label>
                  <input
                    type="text"
                    required
                    autoComplete="off"
                    data-testid="student-first-name"
                    value={stud.first_name}
                    onChange={(e) => setStud({ ...stud, first_name: e.target.value })}
                    placeholder="Ton prénom"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-1.5">Nom</label>
                  <input
                    type="text"
                    required
                    autoComplete="off"
                    data-testid="student-last-name"
                    value={stud.last_name}
                    onChange={(e) => setStud({ ...stud, last_name: e.target.value })}
                    placeholder="Ton nom de famille"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-1.5">Code (4 chiffres)</label>
                  <div className="relative">
                    <input
                      type={showPin ? "text" : "password"}
                      required
                      inputMode="numeric"
                      autoComplete="off"
                      data-testid="student-pin"
                      value={stud.pin}
                      onChange={(e) => setStud({ ...stud, pin: e.target.value })}
                      placeholder="Ton code secret"
                      className={inputClass + " pr-12"}
                    />
                    <button
                      type="button"
                      tabIndex={-1}
                      onClick={() => setShowPin((s) => !s)}
                      aria-label={showPin ? "Masquer le code" : "Afficher le code"}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-orange-500 transition-colors"
                    >
                      {showPin ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                  <p className="text-xs text-slate-400 font-medium mt-1.5">Ton code t'a été donné par ton professeur.</p>
                </div>

                {error && (
                  <p data-testid="login-error" className="text-sm font-bold text-red-600 bg-red-50 rounded-xl px-4 py-2.5">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="student-login-submit"
                  className="w-full flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white rounded-full px-6 py-3.5 font-bold transition-colors shadow-sm shadow-orange-200"
                >
                  {loading ? "Connexion…" : <>Entrer dans le magasin <ArrowRight className="h-4 w-4" /></>}
                </button>
              </form>

              <div className="mt-8 pt-6 border-t border-slate-100 text-center">
                <button
                  type="button"
                  data-testid="show-admin-login"
                  onClick={() => { setMode("admin"); setError(""); }}
                  className="inline-flex items-center gap-2 text-sm font-bold text-slate-500 hover:text-orange-600 transition-colors"
                >
                  <Shield className="h-4 w-4" /> Admin
                </button>
              </div>
            </>
          ) : (
            <>
              <button
                type="button"
                data-testid="admin-login-back"
                onClick={() => { setMode("student"); setError(""); }}
                className="inline-flex items-center gap-1.5 text-sm font-bold text-slate-400 hover:text-slate-700 mb-6 transition-colors"
              >
                <ArrowLeft className="h-4 w-4" /> Retour élève
              </button>
              <div className="h-12 w-12 rounded-2xl bg-orange-100 text-orange-600 flex items-center justify-center mb-4">
                <Shield className="h-6 w-6" />
              </div>
              <h2 className="font-display text-3xl font-semibold text-slate-900">Espace administrateur</h2>
              <p className="text-slate-500 mt-1 font-medium">Entre le code administrateur pour accéder au tableau de bord.</p>

              <form onSubmit={submitAdmin} className="mt-8 space-y-4">
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-1.5">Code administrateur</label>
                  <div className="relative">
                    <input
                      type={showCode ? "text" : "password"}
                      required
                      autoComplete="off"
                      data-testid="admin-code"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      placeholder="Code secret"
                      className={inputClass + " pr-12"}
                    />
                    <button
                      type="button"
                      tabIndex={-1}
                      onClick={() => setShowCode((s) => !s)}
                      aria-label={showCode ? "Masquer le code" : "Afficher le code"}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-orange-500 transition-colors"
                    >
                      {showCode ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                {error && (
                  <p data-testid="admin-login-error" className="text-sm font-bold text-red-600 bg-red-50 rounded-xl px-4 py-2.5">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="admin-login-submit"
                  className="w-full flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white rounded-full px-6 py-3.5 font-bold transition-colors shadow-sm shadow-orange-200"
                >
                  {loading ? "Connexion…" : <>Accéder au tableau de bord <ArrowRight className="h-4 w-4" /></>}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
